/**
    * @description      : 
    * @author           : USER
    * @group            : 
    * @created          : 19/11/2025 - 20:18:01
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 19/11/2025
    * - Author          : USER
    * - Modification    : 
**/
// src/services/api.js - UPDATED with hardcoded production URLs
import axios from 'axios';

// Environment variables (Render + Vercel support)
const ENV_RENDER_API = process.env.REACT_APP_API_URL_RENDER;
const ENV_VERCEL_API = process.env.REACT_APP_API_URL;

// Fallback hardcoded URLs
const VERCEL_API = ENV_VERCEL_API || 'https://african-debaters-backend.vercel.app/api';
const RENDER_API = ENV_RENDER_API || 'https://african-debaters-web-backend.onrender.com/api';
const LOCAL_API = 'http://localhost:5000/api';

// Determine the correct API base URL
let API_BASE_URL = LOCAL_API;

try {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    if (hostname.includes('onrender.com')) {
      API_BASE_URL = RENDER_API;
    } else if (hostname.includes('vercel.app')) {
      API_BASE_URL = VERCEL_API;
    } else if (process.env.NODE_ENV === 'production') {
      // Default to Vercel for production
      API_BASE_URL = VERCEL_API;
    }
  } else if (process.env.NODE_ENV === 'production') {
    // Default to Vercel for production
    API_BASE_URL = VERCEL_API;
  }
} catch {
  // Default to Vercel as fallback instead of local
  API_BASE_URL = VERCEL_API;
}

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false  // CHANGED from default/true to false
});

// Create a separate axios instance for public endpoints (no auth required)
const publicApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false  // CHANGED from default/true to false
});

// NOTE: Automatic Clerk token injection for authenticated requests
// The interceptor checks if Clerk user is available and adds token automatically

// ✅ FIXED: Request interceptor - Always get fresh Clerk token
api.interceptors.request.use(
  async (config) => {
    try {
      // Skip token ONLY for truly public endpoints (not admin endpoints)
      const isPublicGallery = config.url === '/gallery' || config.url?.match(/^\/gallery\/?\?/);
      const isPublicSponsors = config.url === '/sponsors' || (config.url?.startsWith('/sponsors/') && !config.url?.includes('/admin'));

      if (isPublicGallery || isPublicSponsors) {
        return config;
      }

      // Always get fresh token from Clerk to avoid expired token issues
      let token = null;

      // Try to get fresh token from Clerk session
      if (typeof window !== 'undefined' && window.Clerk) {
        try {
          const session = await window.Clerk.session;
          if (session && typeof session.getToken === 'function') {
            // Force a fresh token fetch to ensure it's not expired
            token = await session.getToken({ skipCache: true });

            // Update the cached token for other uses
            if (token) {
              window.__clerk_session_token = token;
            }
          }
        } catch (err) {
          console.warn('⚠️  Failed to get Clerk token:', err.message);
          // Fallback to cached token if Clerk call fails
          if (window.__clerk_session_token) {
            token = window.__clerk_session_token;
          }
        }
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        // Reduce console noise - only log in development
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Token added to request:', config.url);
        }
      } else {
        // Reduce console noise - only warn in development
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️  No Clerk token available for:', config.url);
        }
      }
    } catch (error) {
      console.error('❌ Error getting Clerk token:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ✅ NEW: Helper function to update token globally
export const setClerkToken = (token) => {
  window.__clerk_session_token = token;
};

// ✅ NEW: Helper to clear token
export const clearClerkToken = () => {
  delete window.__clerk_session_token;
};

// Public API interceptor (no auth token added)
publicApi.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling (shared function)
const responseInterceptor = (response) => {
  // Handle different response structures
  if (response.data && typeof response.data === 'object') {
    // If response has success flag, return accordingly
    if (response.data.success !== undefined) {
      return response.data;
    }
    // Otherwise return the data
    return response.data;
  }
  
  return response.data;
};

const errorInterceptor = (error) => {
  // Handle 401 - Unauthorized (Clerk will handle session management)
  if (error.response?.status === 401) {
    // Don't clear any storage - Clerk manages auth state
    // Don't redirect - ProtectedRoute component handles this
    return Promise.reject(new Error('Authentication required. Please sign in.'));
  }

  if (error.response?.status === 403) {
    return Promise.reject(new Error('Access denied. Please check your permissions.'));
  }

  if (error.response?.status === 404) {
    return Promise.reject(new Error('Resource not found.'));
  }

  if (error.response?.status >= 500) {
    return Promise.reject(new Error('Server error. Please try again later.'));
  }

  const errorMessage = error.response?.data?.message ||
                      error.response?.data?.error ||
                      error.message ||
                      'An unexpected error occurred';

  return Promise.reject(new Error(errorMessage));
};

// Apply response interceptors to both instances
api.interceptors.response.use(responseInterceptor, errorInterceptor);
publicApi.interceptors.response.use(responseInterceptor, errorInterceptor);

// Enhanced API Methods with better error handling
export const authAPI = {
  // REPLACE the entire login function (around line 118-160) with:
  login: async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);
      
      // Handle response data
      const data = response.data || response;
      
      // Check for successful login
      if (data.token && data.user) {
        // NOTE: Authentication is now managed by Clerk. Do NOT store auth tokens or profile
        // in localStorage. The Clerk SDK manages the session and tokens; backend calls
        // should use Clerk tokens (via the request interceptor or getToken).
        return {
          success: true,
          token: data.token,
          user: data.user,
          message: data.message || 'Login successful'
        };
      }
      
      // If no token, login failed
      return {
        success: false,
        message: data.message || 'Invalid credentials'
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Login failed'
      };
    }
  },
  register: (userData) => api.post('/auth/register', userData),
  getProfile: async () => {
    try {
      const response = await api.get('/users/me');
      return response;
    } catch (error) {
      throw error;
    }
  },
  forgotPassword: async (data) => {
    try {
      const response = await api.post('/auth/forgot-password', data);
      return response.data || response;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to send reset email');
    }
  },
  verifyResetOTP: async (data) => {
    try {
      const response = await api.post('/auth/verify-reset-otp', data);
      return response.data || response;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Invalid OTP code');
    }
  },
  resetPassword: async (data) => {
    try {
      const response = await api.post('/auth/reset-password', data);
      return response.data || response;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to reset password');
    }
  },
  updateProfile: (data) => api.put('/users/me', data),
  refreshToken: () => api.post('/auth/refresh'),
  changePassword: (passwordData) => api.put('/auth/change-password', passwordData),
  deleteAccount: (data) => api.delete('/auth/delete-account', { data }),
  requestPasswordReset: (data) => api.post('/auth/forgot-password', data),
  verifyEmail: (data) => api.post('/auth/verify-email', data),
  resendVerificationEmail: () => api.post('/auth/resend-verification'),
};

export const eventsAPI = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/events', { 
        params: { 
          ...params, 
          _t: Date.now()
        } 
      });
      
      // Ensure we return a consistent structure
      if (response.success) {
        return {
          success: true,
          events: response.events || [],
          count: response.count || 0,
          total: response.total || 0
        };
      } else if (Array.isArray(response)) {
        return {
          success: true,
          events: response,
          count: response.length,
          total: response.length
        };
      } else {
        return {
          success: true,
          events: [],
          count: 0,
          total: 0
        };
      }
    } catch (error) {
      return {
        success: false,
        events: [],
        count: 0,
        total: 0,
        error: error.message
      };
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/events/${id}`);
      return response;
    } catch (error) {
      throw error;
    }
  },

  register: async (eventId) => {
    try {
      const response = await api.post('/events/register', { eventId });
      return response;
    } catch (error) {
      throw error;
    }
  },

  unregister: async (eventId) => {
    try {
      const response = await api.delete(`/events/${eventId}/unregister`);
      return response;
    } catch (error) {
      throw error;
    }
  },

  getUserEvents: async () => {
    try {
      const response = await api.get('/events/my-events');
      
      // Ensure consistent structure
      if (response.success) {
        return {
          success: true,
          events: response.events || []
        };
      } else if (Array.isArray(response)) {
        return {
          success: true,
          events: response
        };
      } else {
        return {
          success: true,
          events: []
        };
      }
    } catch (error) {
      return {
        success: false,
        events: [],
        error: error.message
      };
    }
  },
  
  adminUnregisterParticipant: async (eventId, participantId) => {
    try {
      // axios instance has a response interceptor that returns response.data
      // so the resolved value here is already the data payload. Return it directly.
      const response = await api.delete(`/events/admin/unregister/${eventId}/${participantId}`);
      return response;
    } catch (error) {
      console.error('Admin unregister participant error:', error);
      throw error;
    }
  },

  getStats: async () => {
    try {
      const response = await api.get('/events/stats');
      return response;
    } catch (error) {
      return {
        success: false,
        stats: {},
        error: error.message
      };
    }
  },
};

// ===== PUBLIC GALLERY API (for Gallery.js page) =====
export const fetchGallery = async () => {
  try {
    console.log('Fetching gallery from:', `${API_BASE_URL}/gallery`);
    const response = await publicApi.get('/gallery');
    console.log('Gallery response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Fetch gallery error:', error);
    return { success: false, gallery: [] };
  }
};

// ===== PUBLIC SPONSORS / PARTNERS API =====
export const fetchSponsors = async (params = {}) => {
  try {
    const response = await publicApi.get('/sponsors', { params });
    // response interceptor unwraps response.data, but ensure consistent shape
    if (response && response.sponsors) return { success: true, sponsors: response.sponsors };
    if (Array.isArray(response)) return { success: true, sponsors: response };
    return { success: true, sponsors: [] };
  } catch (error) {
    console.error('Fetch sponsors error:', error);
    return { success: false, sponsors: [], error: error.message };
  }
};

export const usersAPI = {
  getStats: async () => {
    try {
      const response = await api.get('/users/stats');
      
      if (response.success) {
        return response;
      } else {
        return {
          success: true,
          stats: response.stats || response || {}
        };
      }
    } catch (error) {
      return {
        success: false,
        stats: {},
        error: error.message
      };
    }
  },

  getWinners: async () => {
    try {
      const response = await api.get('/users/winners');
      
      if (response.success) {
        return {
          success: true,
          winners: response.winners || []
        };
      } else if (Array.isArray(response)) {
        return {
          success: true,
          winners: response
        };
      } else {
        return {
          success: true,
          winners: []
        };
      }
    } catch (error) {
      return {
        success: false,
        winners: [],
        error: error.message
      };
    }
  },

  getProfile: async () => {
    try {
      const response = await api.get('/users/me');
      return response;
    } catch (error) {
      throw error;
    }
  },

  updateProfile: async (data) => {
    try {
      const response = await api.put('/users/me', data);
      return response;
    } catch (error) {
      throw error;
    }
  },
};

export const notificationsAPI = {
  getAll: async () => {
    try {
      const response = await api.get('/notifications');
      
      if (response.success) {
        return {
          success: true,
          notifications: response.notifications || []
        };
      } else if (Array.isArray(response)) {
        return {
          success: true,
          notifications: response
        };
      } else {
        return {
          success: true,
          notifications: []
        };
      }
    } catch (error) {
      return {
        success: false,
        notifications: [],
        error: error.message
      };
    }
  },

  markAsRead: async (id) => {
    try {
      const response = await api.put(`/notifications/${id}/read`);
      return response;
    } catch (error) {
      throw error;
    }
  },

  markAllAsRead: async () => {
    try {
      const response = await api.put('/notifications/read-all');
      return response;
    } catch (error) {
      throw error;
    }
  },
};

export const liveAPI = {
  getUserResults: async (sessionId, userId) => {
    try {
      const response = await api.get(`/live/results/${sessionId}/${userId}`);
      return response;
    } catch (error) {
      throw error;
    }
  },

  getLeaderboard: async (sessionId) => {
    try {
      const response = await api.get(`/live/leaderboard/${sessionId}`);
      return response;
    } catch (error) {
      throw error;
    }
  },

  getLiveSessions: async () => {
    try {
      const response = await api.get('/live/sessions');
      return response;
    } catch (error) {
      throw error;
    }
  },
  
  createSession: (data) => api.post('/live/sessions', data),
  toggleSession: (sessionId) => api.put(`/live/sessions/${sessionId}/toggle`),
  updateScore: (data) => api.post('/live/scores', data),
  publishScores: (sessionId, round) => api.put(`/live/scores/publish/${sessionId}`, { round })
};

export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  
  // Events Management - UPDATED ENDPOINTS
  getEvents: (params = {}) => {
    return api.get('/admin/events', { 
      params: {
        ...params,
        populate: 'participants'
      }
    });
  },
  
  createEvent: async (formData) => {
    try {
      // Get Clerk token
      let token = null;
      if (typeof window !== 'undefined' && window.Clerk) {
        const session = await window.Clerk.session;
        if (session) {
          token = await session.getToken();
        }
      }

      const response = await fetch(`${API_BASE_URL}/events/admin/create`, {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create event');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  },

  updateEvent: async (eventId, formData) => {
    try {
      // Get Clerk token
      let token = null;
      if (typeof window !== 'undefined' && window.Clerk) {
        const session = await window.Clerk.session;
        if (session) {
          token = await session.getToken();
        }
      }

      const response = await fetch(`${API_BASE_URL}/events/admin/update/${eventId}`, {
        method: 'PUT',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update event');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  },

  // ✅ CONSOLIDATED - Single updateParticipantPayment method for events
  updateParticipantPayment: async (eventId, participantId, paymentData) => {
    try {
      // Get Clerk token
      let token = null;
      if (typeof window !== 'undefined' && window.Clerk) {
        const session = await window.Clerk.session;
        if (session) {
          token = await session.getToken();
        }
      }

      const response = await fetch(`${API_BASE_URL}/events/admin/payment/${eventId}/${participantId}`, {
        method: 'PUT',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update payment');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  },

  // ✅ NEW - Program enrollment payment update
  updateProgramEnrollmentPayment: async (programId, enrollmentId, paymentData) => {
    try {
      // Get Clerk token
      let token = null;
      if (typeof window !== 'undefined' && window.Clerk) {
        const session = await window.Clerk.session;
        if (session) {
          token = await session.getToken();
        }
      }

      const response = await fetch(`${API_BASE_URL}/admin/programs/${programId}/enrollments/${enrollmentId}/payment`, {
        method: 'PUT',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update program payment');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  },


  unregisterParticipant: async (eventId, participantId) => {
    try {
      // Get Clerk token
      let token = null;
      if (typeof window !== 'undefined' && window.Clerk) {
        const session = await window.Clerk.session;
        if (session) {
          token = await session.getToken();
        }
      }

      const response = await fetch(`${API_BASE_URL}/events/admin/${eventId}/participants/${participantId}`, {
        method: 'DELETE',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to remove participant');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  archiveEvent: async (eventId) => {
    try {
      // Get Clerk token
      let token = null;
      if (typeof window !== 'undefined' && window.Clerk) {
        const session = await window.Clerk.session;
        if (session) {
          token = await session.getToken();
        }
      }

      const response = await fetch(`${API_BASE_URL}/events/admin/archive/${eventId}`, {
        method: 'PUT',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to archive event');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  },

  deleteEvent: async (eventId) => {
    try {
      // Get Clerk token
      let token = null;
      if (typeof window !== 'undefined' && window.Clerk) {
        const session = await window.Clerk.session;
        if (session) {
          token = await session.getToken();
        }
      }

      const response = await fetch(`${API_BASE_URL}/events/admin/delete/${eventId}`, {
        method: 'DELETE',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete event');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  updateEventWinners: async (eventId, winners) => {
    try {
      const response = await api.put(`/admin/events/${eventId}/winners`, { 
        winners: winners.map(winner => ({
          ...winner,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }))
      });
      return response.data || response;
    } catch (error) {
      throw error;
    }
  },
  
  // Users Management
  getUsers: (params = {}) => {
    return api.get('/admin/users', {
      params: {
        ...params,
        select: 'name teamName schoolName email phone registrationType status createdAt'
      }
    });
  },

  approveUser: (id) => api.put(`/admin/users/${id}/approve`),
  deleteUser: (id) => api.delete(`/users/${id}`),
  
  // Notifications
  getNotifications: (params = {}) => api.get('/admin/notifications', { params }),
  sendNotification: (notificationData) => api.post('/admin/notifications', notificationData),
  
  // Gallery
  getGallery: () => api.get('/gallery'),
  // Team members (admin)
  getTeamMembers: (params = {}) => api.get('/team/admin/all', { params }),
  createTeamMember: async (formData) => {
    // formData should be a FormData instance when including a photo
    let token = null;
    if (typeof window !== 'undefined' && window.Clerk) {
      const session = await window.Clerk.session;
      if (session) token = await session.getToken();
    }

    const response = await fetch(`${API_BASE_URL}/team/admin/create`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create team member');
    }

    return await response.json();
  },
  updateTeamMember: async (id, formData) => {
    let token = null;
    if (typeof window !== 'undefined' && window.Clerk) {
      const session = await window.Clerk.session;
      if (session) token = await session.getToken();
    }

    const response = await fetch(`${API_BASE_URL}/team/admin/update/${id}`, {
      method: 'PUT',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update team member');
    }

    return await response.json();
  },
  deleteTeamMember: async (id) => {
    return api.delete(`/team/admin/delete/${id}`);
  },
  // Sponsors / Partners (admin)
  getSponsors: (params = {}) => api.get('/sponsors/admin/all', { params }),
  createSponsor: async (formData) => {
    let token = null;
    if (typeof window !== 'undefined' && window.Clerk) {
      const session = await window.Clerk.session;
      if (session) token = await session.getToken();
    }

    const response = await fetch(`${API_BASE_URL}/sponsors/admin`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create sponsor');
    }

    return await response.json();
  },
  updateSponsor: async (id, formData) => {
    let token = null;
    if (typeof window !== 'undefined' && window.Clerk) {
      const session = await window.Clerk.session;
      if (session) token = await session.getToken();
    }

    const response = await fetch(`${API_BASE_URL}/sponsors/admin/${id}`, {
      method: 'PUT',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update sponsor');
    }

    return await response.json();
  },
  deleteSponsor: (id) => api.delete(`/sponsors/admin/${id}`),
  updateSponsorOrder: (id, displayOrder) => api.put(`/sponsors/admin/order/${id}`, { displayOrder }),
  uploadGalleryImage: async (formData) => {
    const response = await api.post('/gallery', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response;
  },
  uploadMultipleGalleryImages: async (formData) => {
    const response = await api.post('/gallery/multiple', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response;
  },
  uploadGalleryVideo: async (formData) => {
    const response = await api.post('/gallery/video', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response;
  },
  deleteGalleryImage: (id) => api.delete(`/gallery/${id}`),
  
  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (settings) => api.put('/admin/settings', settings),
  
  // Programs Management
  getPrograms: (params = {}) => {
    return api.get('/programs/admin/all', { 
      params: {
        ...params,
        populate: 'enrolledUsers'
      }
    });
  },

  createProgram: async (formData) => {
    const response = await api.post('/programs', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },

  updateProgram: async (id, formData) => {
    const response = await api.put(`/programs/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },

  archiveProgram: (id) => api.put(`/programs/${id}/archive`),
  deleteProgram: (id) => api.delete(`/programs/${id}`),
  getProgramEnrollments: (programId) => api.get(`/programs/${programId}/enrollments`)
};

export const programsAPI = {
  // PUBLIC: Get all programs (no authentication required)
  getAll: async (params = {}) => {
    try {
      const response = await publicApi.get('/programs', { params });
      
      if (response.success) {
        return {
          success: true,
          programs: response.programs || []
        };
      } else if (Array.isArray(response)) {
        return {
          success: true,
          programs: response
        };
      } else {
        return {
          success: true,
          programs: []
        };
      }
    } catch (error) {
      return {
        success: false,
        programs: [],
        error: error.message
      };
    }
  },

  getById: async (id) => {
    try {
      const response = await publicApi.get(`/programs/${id}`);
      return response;
    } catch (error) {
      throw error;
    }
  },

  getStats: async () => {
    try {
      const response = await publicApi.get('/programs/stats');
      
      if (response.success) {
        return response;
      } else {
        return {
          success: true,
          stats: response.stats || response || {}
        };
      }
    } catch (error) {
      return {
        success: false,
        stats: {
          totalSchools: 100,
          totalStudents: 5000,
          totalPrograms: 6
        },
        error: error.message
      };
    }
  },

  // PROTECTED: Enroll in a program
  enroll: async (programId) => {
    try {
      const response = await api.post(`/programs/${programId}/enroll`);
      return response;
    } catch (error) {
      throw error;
    }
  },

  getUserPrograms: async () => {
    try {
      const response = await api.get('/programs/user/enrolled');
      
      if (response.success) {
        return {
          success: true,
          programs: response.programs || []
        };
      } else if (Array.isArray(response)) {
        return {
          success: true,
          programs: response
        };
      } else {
        return {
          success: true,
          programs: []
        };
      }
    } catch (error) {
      return {
        success: false,
        programs: [],
        error: error.message
      };
    }
  },

  // PROTECTED: Admin functions
  create: async (programData) => {
    try {
      const config = {};
      if (programData instanceof FormData) {
        config.headers = { 'Content-Type': 'multipart/form-data' };
      }
      
      const response = await api.post('/programs', programData, config);
      return response;
    } catch (error) {
      throw error;
    }
  },

  update: async (id, programData) => {
    try {
      const config = {};
      if (programData instanceof FormData) {
        config.headers = { 'Content-Type': 'multipart/form-data' };
      }
      
      const response = await api.put(`/programs/${id}`, programData, config);
      return response;
    } catch (error) {
      throw error;
    }
  },

  delete: (id) => api.delete(`/programs/${id}`),
  archive: (id) => api.put(`/programs/${id}/archive`),
  getAdminStats: () => api.get('/programs/admin/stats'),

  // Admin unenroll user from program
  adminUnenrollUser: async (programId, userId) => {
    try {
      const response = await api.delete(`/programs/admin/${programId}/unenroll/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Admin unenroll user error:', error);
      throw error;
    }
  },

  // User unenroll from program
  unenroll: async (programId) => {
    try {
      const response = await api.delete(`/programs/${programId}/unenroll`);
      return response.data;
    } catch (error) {
      console.error('Unenroll from program error:', error);
      throw error;
    }
  }
};

// Test function to check API connectivity
export const testAPI = async () => {
  try {
    const response = await publicApi.get('/test');
    return true;
  } catch (error) {
    return false;
  }
};

export default api;
